import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import banko.composeapp.generated.resources.Res
import banko.composeapp.generated.resources.*
import dev.gitlive.firebase.firestore.Timestamp
import models.CareerGame
import models.CareerStats
import models.Rival
import org.jetbrains.compose.resources.stringResource

/**
 * The home screen's entry into lifetime stats: a card with a one-line summary that opens
 * [CareerStatsSheet]. Before the first finished game it explains what will show up there.
 */
@Composable
internal fun YourStatsRow(modifier: Modifier = Modifier) {
    val career by CareerStatsStore.stats.collectAsState()
    var showCareer by remember { mutableStateOf(false) }
    Surface(
        shape = RoundedCornerShape(16.dp),
        color = MaterialTheme.colorScheme.surfaceContainerHigh,
        shadowElevation = 2.dp,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        modifier = modifier
            .clip(RoundedCornerShape(16.dp))
            .clickable {
                showCareer = true
                Analytics.statsViewed(Analytics.StatsSource.Home)
            },
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 16.dp, vertical = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Icon(
                imageVector = BankoIcons.BarChart,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.primary,
            )
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = stringResource(Res.string.your_stats),
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold,
                    color = MaterialTheme.colorScheme.onSurface,
                )
                Text(
                    text = if (career.gamesPlayed == 0) {
                        stringResource(Res.string.your_stats_row_empty)
                    } else {
                        stringResource(
                            Res.string.your_stats_row_summary,
                            career.gamesPlayed,
                            career.wins,
                            "${career.winRatePercent ?: 0}%",
                        )
                    },
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.85f),
                )
            }
            Icon(
                imageVector = BankoIcons.ChevronRight,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.primary,
            )
        }
    }
    if (showCareer) {
        CareerStatsSheet(onDismiss = { showCareer = false })
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
internal fun CareerStatsSheet(onDismiss: () -> Unit) {
    val sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
    val career by CareerStatsStore.stats.collectAsState()
    ModalBottomSheet(
        modifier = Modifier.statusBarsPadding(),
        onDismissRequest = onDismiss,
        sheetState = sheetState,
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .verticalScroll(rememberScrollState())
                .navigationBarsPadding()
                .padding(horizontal = 16.dp)
                .padding(bottom = 24.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
                Text(
                    text = stringResource(Res.string.your_stats),
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.SemiBold,
                    color = MaterialTheme.colorScheme.onSurface,
                )
                Text(
                    text = stringResource(Res.string.career_device_note),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
            if (career.gamesPlayed == 0) {
                StatsCard {
                    Text(
                        text = stringResource(Res.string.career_empty_title),
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.SemiBold,
                        color = MaterialTheme.colorScheme.onSurface,
                    )
                    Text(
                        text = stringResource(Res.string.career_empty_body),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            } else {
                CareerContent(career)
            }
        }
    }
}

@Composable
private fun CareerContent(career: CareerStats) {
    val nowSeconds = remember { Timestamp.now().seconds }
    val dash = stringResource(Res.string.stats_dash)

    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        StatTile(stringResource(Res.string.career_games), career.gamesPlayed.toString(), Modifier.weight(1f))
        StatTile(stringResource(Res.string.career_wins), career.wins.toString(), Modifier.weight(1f))
        StatTile(
            stringResource(Res.string.career_win_rate),
            career.winRatePercent?.let { "$it%" } ?: dash,
            Modifier.weight(1f),
        )
    }
    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        StatTile(stringResource(Res.string.career_best_score), career.bestScore.toString(), Modifier.weight(1f))
        StatTile(stringResource(Res.string.career_avg_score), career.averageScore?.toString() ?: dash, Modifier.weight(1f))
        StatTile(
            stringResource(Res.string.career_best_bank),
            if (career.banks > 0) career.bestBank.toString() else dash,
            Modifier.weight(1f),
        )
    }
    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        StatTile(stringResource(Res.string.career_win_streak), career.currentWinStreak.toString(), Modifier.weight(1f))
        StatTile(stringResource(Res.string.career_best_streak), career.bestWinStreak.toString(), Modifier.weight(1f))
        StatTile(stringResource(Res.string.stats_avg_bank), career.averageBank?.toString() ?: dash, Modifier.weight(1f))
    }

    RecentFormCard(career.recentGames.take(RECENT_FORM_GAMES))

    if (career.recentGames.size >= 2) {
        StatsCard {
            StatsSectionTitle(stringResource(Res.string.career_section_scores))
            RecentScoresChart(
                games = career.recentGames.take(RECENT_SCORES_GAMES),
                nowEpochSeconds = nowSeconds,
            )
        }
    }

    StatsCard {
        StatsSectionTitle(stringResource(Res.string.career_section_placements))
        val maxCount = career.placements.maxOrNull()?.coerceAtLeast(1) ?: 1
        val placementColor = seriesColor(0)
        career.placements.forEachIndexed { index, count ->
            if (count > 0) {
                val isLastBucket = index == CareerStats.MAX_PLACEMENT_BUCKETS - 1
                HorizontalBarRow(
                    label = ordinalLabel(index + 1) + if (isLastBucket) "+" else "",
                    value = count.toString(),
                    fraction = count.toFloat() / maxCount,
                    color = placementColor,
                )
            }
        }
    }

    val averageRolls = career.averageRollsBeforeBank
    val style = career.style
    StatsCard {
        StatsSectionTitle(stringResource(Res.string.career_section_style))
        if (averageRolls != null && style != null) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                StyleChip(style)
            }
            Text(
                text = stringResource(
                    Res.string.career_style_body,
                    formatOneDecimal(averageRolls),
                    career.banks,
                    career.averageBank ?: 0,
                ),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurface,
            )
            // Nerve scale from "banks right after the safe rolls" (3) to "rides a dozen rolls" (12).
            ScaleMeter(
                fraction = ((averageRolls - 3.0) / 9.0).toFloat(),
                lowLabel = stringResource(Res.string.career_style_scale_low),
                highLabel = stringResource(Res.string.career_style_scale_high),
            )
        }
        career.bustRatePercent?.let { rate ->
            Text(
                text = stringResource(Res.string.career_bust_rate, "$rate%", career.timesBusted),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }

    StatsCard {
        StatsSectionTitle(stringResource(Res.string.career_section_luck))
        val sevenLuck = career.sevenLuckPoints
        val doublesLuck = career.doublesLuckPoints
        if (sevenLuck == null || doublesLuck == null || career.rolls == 0) {
            Text(
                text = stringResource(
                    Res.string.career_luck_needs_rolls,
                    (CareerStats.MIN_ROLLS_FOR_LUCK - career.rolls).coerceAtLeast(1),
                ),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        } else {
            Text(
                text = when {
                    doublesLuck >= LUCK_VERDICT_POINTS -> stringResource(Res.string.career_luck_verdict_lucky)
                    doublesLuck <= -LUCK_VERDICT_POINTS -> stringResource(Res.string.career_luck_verdict_unlucky)
                    else -> stringResource(Res.string.career_luck_verdict_fair)
                },
                style = MaterialTheme.typography.titleSmall,
                fontWeight = FontWeight.SemiBold,
                color = MaterialTheme.colorScheme.onSurface,
            )
            Text(
                text = stringResource(
                    Res.string.career_luck_sevens,
                    formatPercent(career.sevens.toDouble() / career.rolls),
                ),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurface,
            )
            Text(
                text = stringResource(
                    Res.string.career_luck_doubles,
                    formatPercent(career.doubles.toDouble() / career.rolls),
                ),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurface,
            )
            Text(
                text = stringResource(Res.string.career_luck_fair),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }

    RivalsCard(career)

    StatsCard {
        StatsSectionTitle(stringResource(Res.string.career_section_history))
        career.recentGames.take(HISTORY_GAMES).forEachIndexed { index, game ->
            if (index > 0) HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
            GameHistoryRow(game, nowSeconds)
        }
    }

    var confirmReset by remember { mutableStateOf(false) }
    TextButton(
        onClick = { confirmReset = true },
        modifier = Modifier.fillMaxWidth(),
    ) {
        Text(
            text = stringResource(Res.string.career_reset),
            color = MaterialTheme.colorScheme.error,
        )
    }
    if (confirmReset) {
        AlertDialog(
            onDismissRequest = { confirmReset = false },
            title = { Text(stringResource(Res.string.career_reset_title)) },
            text = { Text(stringResource(Res.string.career_reset_message)) },
            confirmButton = {
                TextButton(
                    onClick = {
                        Analytics.careerStatsReset(career.gamesPlayed)
                        CareerStatsStore.reset()
                        confirmReset = false
                    },
                ) {
                    Text(
                        text = stringResource(Res.string.career_reset_confirm),
                        color = MaterialTheme.colorScheme.error,
                    )
                }
            },
            dismissButton = {
                TextButton(onClick = { confirmReset = false }) {
                    Text(stringResource(Res.string.career_reset_cancel))
                }
            },
        )
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun RecentFormCard(recentNewestFirst: List<CareerGame>) {
    if (recentNewestFirst.isEmpty()) return
    val chronological = recentNewestFirst.reversed()
    StatsCard {
        StatsSectionTitle(stringResource(Res.string.career_section_form))
        FlowRow(
            horizontalArrangement = Arrangement.spacedBy(6.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            chronological.forEachIndexed { index, game ->
                val latest = index == chronological.lastIndex
                Surface(
                    shape = RoundedCornerShape(8.dp),
                    color = if (game.won) {
                        MaterialTheme.colorScheme.primaryContainer
                    } else {
                        MaterialTheme.colorScheme.surfaceContainerHighest
                    },
                    contentColor = if (game.won) {
                        MaterialTheme.colorScheme.onPrimaryContainer
                    } else {
                        MaterialTheme.colorScheme.onSurfaceVariant
                    },
                    border = BorderStroke(
                        width = if (latest) 2.dp else 1.dp,
                        color = if (latest) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.outlineVariant,
                    ),
                ) {
                    Text(
                        text = ordinalLabel(game.placement),
                        style = MaterialTheme.typography.labelMedium,
                        fontWeight = if (game.won) FontWeight.Bold else FontWeight.Medium,
                        textAlign = TextAlign.Center,
                        modifier = Modifier
                            .widthIn(min = 34.dp)
                            .padding(horizontal = 6.dp, vertical = 5.dp),
                    )
                }
            }
        }
        Text(
            text = stringResource(Res.string.career_form_hint),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.8f),
        )
    }
}

@Composable
private fun RivalsCard(career: CareerStats) {
    val youColor = seriesColor(0)
    val themColor = seriesColor(1)
    StatsCard {
        StatsSectionTitle(stringResource(Res.string.career_section_rivals))
        val rivals = career.topRivals.take(TOP_RIVALS)
        if (rivals.isEmpty()) {
            Text(
                text = stringResource(Res.string.career_rivals_empty),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        } else {
            RivalRows(rivals, youColor, themColor)
        }
    }
}

@Composable
private fun RivalRows(rivals: List<Rival>, youColor: Color, themColor: Color) {
    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            SeriesDot(youColor, size = 8.dp)
            Text(
                text = stringResource(Res.string.career_rivals_legend_you),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            Spacer(modifier = Modifier.width(8.dp))
            SeriesDot(themColor, size = 8.dp)
            Text(
                text = stringResource(Res.string.career_rivals_legend_them),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
        rivals.forEach { rival ->
            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        text = rival.name,
                        style = MaterialTheme.typography.titleSmall,
                        fontWeight = FontWeight.SemiBold,
                        color = MaterialTheme.colorScheme.onSurface,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.weight(1f),
                    )
                    Text(
                        text = stringResource(
                            Res.string.career_rival_record,
                            rival.winsAgainst,
                            rival.lossesAgainst,
                            rival.games,
                        ),
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
                SplitBar(
                    left = rival.winsAgainst,
                    right = rival.lossesAgainst,
                    leftColor = youColor,
                    rightColor = themColor,
                )
            }
        }
    }
}

@Composable
private fun GameHistoryRow(game: CareerGame, nowSeconds: Long) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = if (game.won) {
                    stringResource(Res.string.career_history_win, game.score)
                } else {
                    stringResource(
                        Res.string.career_history_place,
                        ordinalLabel(game.placement),
                        game.playerCount,
                        game.score,
                    )
                },
                style = MaterialTheme.typography.titleSmall,
                fontWeight = FontWeight.SemiBold,
                color = MaterialTheme.colorScheme.onSurface,
            )
            val age = ageLabel(nowSeconds, game.playedAtEpochSeconds)
            Text(
                text = if (game.online) {
                    stringResource(Res.string.career_history_meta_online, game.rounds, age)
                } else {
                    stringResource(Res.string.career_history_meta_local, game.rounds, age)
                },
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
        Spacer(modifier = Modifier.size(8.dp))
        Text(
            text = if (game.won) {
                stringResource(Res.string.career_history_margin, game.score - game.runnerUpScore)
            } else {
                stringResource(Res.string.career_history_winner, game.winnerName, game.winnerScore)
            },
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            textAlign = TextAlign.End,
            maxLines = 2,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.widthIn(max = 140.dp),
        )
    }
}

private const val RECENT_FORM_GAMES = 10
private const val RECENT_SCORES_GAMES = 20
private const val HISTORY_GAMES = 10
private const val TOP_RIVALS = 5

// Doubles this many percentage points above/below the fair 1-in-6 earn a "hot"/"cold" verdict.
private const val LUCK_VERDICT_POINTS = 3
